import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Sentry } from '../lib/sentry';
import { clearStaleBuildState } from '../utils/sw-register';
import { ErrorFallback } from './ui/ErrorFallback';

interface Props {
  children: ReactNode;
  /** Optional custom fallback. Receives the error and a retry callback. */
  fallback?: (error: Error | null, retry: () => void) => ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

const STALE_CHUNK_RELOAD_KEY = 'hsy:stale-chunk-reload-signature';

export function isStaleChunkError(error: unknown) {
  if (!error) return false;
  const message = error instanceof Error
    ? `${error.name} ${error.message}`.toLowerCase()
    : String(error).toLowerCase();

  return [
    'failed to fetch dynamically imported module',
    'importing a module script failed',
    'error loading dynamically imported module',
    'chunkloaderror',
    'loading chunk',
    // MIME-type mismatch surfaces when Cloudflare's SPA fallback returns
    // index.html for a missing /assets/*.js chunk. Each browser phrases it
    // differently; match all three.
    "'text/html' is not a valid javascript mime type", // Safari/WebKit
    'not a valid javascript mime type',                // Safari/WebKit (looser)
    'expected a javascript module',                     // Chrome/Blink
    'failed to load module script',                     // Chrome/Blink (alt)
    'disallowed mime type',                             // Firefox/Gecko
  ].some(fragment => message.includes(fragment));
}

function getReloadedUrl() {
  try {
    return window.sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY);
  } catch {
    return null;
  }
}

function markReloadedUrl(url: string) {
  try {
    window.sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, url);
  } catch {
    // A reload is still the safest recovery even if sessionStorage is blocked.
  }
}

export function recoverFromStaleChunkError(error: unknown) {
  if (typeof window === 'undefined' || !isStaleChunkError(error)) return false;

  const message = error instanceof Error ? error.message : String(error);
  const reloadSignature = `${window.location.href}|${message}`;
  if (getReloadedUrl() === reloadSignature) return false;

  markReloadedUrl(reloadSignature);
  // Clear shell/asset caches + unregister SW before reload. Otherwise Safari
  // can keep handing back a stale /index.html that still points at the
  // missing chunk, looping the user back into the error UI.
  void clearStaleBuildState().finally(() => {
    window.location.reload();
  });
  return true;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (recoverFromStaleChunkError(error)) return;

    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo.componentStack ?? '' } },
    });
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.handleRetry);
      }

      return (
        <ErrorFallback
          error={this.state.error}
          onRetry={this.handleRetry}
        />
      );
    }

    return this.props.children;
  }
}
